import {
  FC,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { RovingFocusGroup, RovingFocusGroupItem } from '@radix-ui/react-roving-focus';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { compact } from 'lodash';

import { icon_arrow_left, icon_arrow_right } from '@gn-audio/icons';
import { Button } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';
import { ButtonVariantType } from '@gn-audio/jss-components/components/Button/Button';

import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

export interface JabraSlideProps {
  key: string;
  children: ReactNode;
  renderThumbnail?: (isActive: boolean) => ReactNode;
  'data-single-slide-classname'?: string;
}

export const JabraSlide: FC<JabraSlideProps> = ({ children }) => {
  return <>{children}</>;
};

export interface JabraSliderProps {
  children: (
    | ReactElement<JabraSlideProps, typeof JabraSlide>
    | ReactElement<{ key: string }>
    | null
  )[];
  sliderSettings?: EmblaOptionsType;
  className?: string;
  mainClassName?: string;
  footerClassName?: string;
  slideClassName?: string;
  startIndex?: number;
  showSlideArrows?: boolean;
  slideArrowsAlign?: 'inline' | 'bottom';
  customButton?: (
    direction: 'left' | 'right',
    isEnabled: boolean,
    clickHandler: () => void
  ) => ReactElement;
  showThumbnailArrows?: boolean;
  thumbnailClassName?: string;
  onChange?: (selectedIndex: number) => void;
  showSlideArrowsOnMobile?: boolean;
  overflow?: boolean;
  buttonVariant?: ButtonVariantType;
}

export interface JabraSliderRef {
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
}

const JabraSliderComponent = forwardRef<JabraSliderRef, JabraSliderProps>(
  (
    {
      children: slides,
      sliderSettings,
      customButton,
      slideArrowsAlign = 'inline',
      showSlideArrows = true,
      showThumbnailArrows = false,
      className,
      footerClassName,
      mainClassName,
      slideClassName,
      startIndex = 0,
      onChange,
      showSlideArrowsOnMobile = false,
      overflow = false,
      buttonVariant = 'tertiary',
    },
    ref
  ) => {
    const { t } = useFallbackTranslation();
    const [internalSelectedIndex, setInternalSelectedIndex] = useState(startIndex);
    const [emblaRef, emblaMainApi] = useEmblaCarousel({
      startIndex: startIndex,
      ...sliderSettings,
      watchDrag(emblaApi) {
        return emblaApi.canScrollPrev() || emblaApi.canScrollNext();
      },
    });
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
      containScroll: 'keepSnaps',
      dragFree: true,
    });
    useEffect(() => {
      setInternalSelectedIndex(startIndex);
    }, [startIndex, setInternalSelectedIndex]);

    const handleThumbnailClick = useCallback(
      (index: number) => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollTo(index);
      },
      [emblaMainApi]
    );

    useImperativeHandle(ref, () => {
      return {
        next: () => emblaMainApi?.scrollNext(),
        prev: () => emblaMainApi?.scrollPrev(),
        goTo: (index: number) => emblaMainApi?.scrollTo(index),
      };
    }, [emblaMainApi]);

    useEffect(() => {
      if (!emblaMainApi) return;

      const onSelect = () => {
        const newIndex = emblaMainApi.selectedScrollSnap();
        setInternalSelectedIndex(newIndex);
        emblaThumbsApi && emblaThumbsApi.scrollTo(newIndex);
        onChange && onChange(newIndex);
      };

      onSelect();
      emblaMainApi.on('select', onSelect);
      emblaMainApi.on('reInit', onSelect);
      return () => {
        emblaMainApi.off('select', onSelect);
        emblaMainApi.off('reInit', onSelect);
      };
    }, [emblaMainApi, emblaThumbsApi, onChange]);

    const canGoForward = emblaMainApi?.canScrollNext() || false;
    const canGoBackward = emblaMainApi?.canScrollPrev() || false;
    const canBeScrolled = showSlideArrows && (canGoForward || canGoBackward);

    const handleGoForward = useCallback(() => {
      emblaMainApi?.scrollNext();
    }, [emblaMainApi]);

    const handleGoBackward = useCallback(() => {
      emblaMainApi?.scrollPrev();
    }, [emblaMainApi]);

    const hasThumbnails = compact(slides).some(
      (slide) => 'renderThumbnail' in slide.props && slide.props.renderThumbnail
    );

    const PreviousButton = useCallback(() => {
      return customButton ? (
        customButton('left', canGoBackward, handleGoBackward)
      ) : (
        <Button
          variant={buttonVariant}
          size="l"
          className={classNames('mr-2 lg:block', {
            '!opacity-50': !canGoBackward,
            hidden: !showSlideArrowsOnMobile,
          })}
          disabled={!canGoBackward}
          aria-label={t('system.Previous')}
          icon={<JabraIcon className="flex h-[1.75rem]" Icon={icon_arrow_left} />}
          onClick={handleGoBackward}
        />
      );
    }, [canGoBackward, customButton, handleGoBackward, buttonVariant, showSlideArrowsOnMobile, t]);

    const NextButton = useCallback(() => {
      return customButton ? (
        customButton('right', canGoForward, handleGoForward)
      ) : (
        <Button
          variant={buttonVariant}
          size="l"
          className={classNames('lg:block', {
            '!opacity-50': !canGoForward,
            hidden: !showSlideArrowsOnMobile,
          })}
          disabled={!canGoForward}
          aria-label={t('system.Next')}
          icon={<JabraIcon className="flex h-[1.75rem]" Icon={icon_arrow_right} />}
          onClick={handleGoForward}
        />
      );
    }, [canGoForward, customButton, handleGoForward, buttonVariant, showSlideArrowsOnMobile, t]);

    return (
      <article className={classNames('flex select-none flex-col items-center', className)}>
        <div className={mainClassName}>
          {canBeScrolled && slideArrowsAlign === 'inline' && <PreviousButton />}
          <div className={`grow ${overflow ? '' : 'overflow-hidden'}`} ref={emblaRef}>
            <ul className="flex">
              {compact(slides).map((slideElement) => {
                const slideProps = slideElement?.props as JabraSlideProps;
                return (
                  <li
                    key={slideElement.key}
                    className={classNames(
                      'flex flex-shrink-0 flex-grow-0 justify-center',
                      slideClassName,
                      slideProps?.['data-single-slide-classname']
                    )}
                  >
                    {slideElement}
                  </li>
                );
              })}
            </ul>
          </div>
          {canBeScrolled && slideArrowsAlign === 'inline' && <NextButton />}
        </div>

        {canBeScrolled && slideArrowsAlign === 'bottom' && (
          <footer className={classNames('flex overflow-visible', footerClassName)}>
            <PreviousButton /> <NextButton />
          </footer>
        )}

        {hasThumbnails && slides.length > 1 && (
          <footer
            className={classNames('flex justify-between gap-4 overflow-visible', footerClassName)}
          >
            {showThumbnailArrows && <PreviousButton />}

            <span className="block flex-1" />
            <div ref={emblaThumbsRef} className="max-w-[20rem] flex-grow-0 overflow-hidden">
              <RovingFocusGroup loop={false} asChild orientation="horizontal">
                <ul
                  className="flex flex-grow-0"
                  role="listbox"
                  aria-label={`${t('system.Thumbnail')} ${t('system.Section')}`}
                >
                  {compact(slides).map((slide, index) => {
                    const selected = internalSelectedIndex === index;

                    const renderThumbnailFunc =
                      'renderThumbnail' in slide.props ? slide.props.renderThumbnail : undefined;
                    return (
                      <li key={slide.key} className="flex flex-shrink-0 basis-1/4 justify-center">
                        <RovingFocusGroupItem
                          active={selected}
                          focusable={true}
                          asChild
                          tabStopId={`${index}`}
                          onFocus={() => handleThumbnailClick(index)}
                        >
                          <button
                            className="group/slider-thumbnail mx-2 my-1 focus-visible:outline-0"
                            onClick={() => handleThumbnailClick(index)}
                            aria-label={`${t('system.Thumbnail')} ${index + 1}`}
                          >
                            {renderThumbnailFunc && renderThumbnailFunc(selected)}
                          </button>
                        </RovingFocusGroupItem>
                      </li>
                    );
                  })}
                </ul>
              </RovingFocusGroup>
            </div>

            <span className="block flex-1" />
            {showThumbnailArrows && <NextButton />}
          </footer>
        )}
      </article>
    );
  }
);
JabraSliderComponent.displayName = 'JabraSlider';
export const JabraSlider = JabraSliderComponent;
